import {
    createContext,
    useContext,
    useState,
    Dispatch,
    SetStateAction,
    useEffect,
    ReactNode,
    useRef,
    useMemo,
    useCallback,
} from 'react'
import { NationalityEnum } from './supplier_context'
import { useLoading } from './loading_context'
import { useSnack } from './snack_context'
import {
    ClientDocumentAlreadyInUseError,
    ClientEmailAlreadyInUseError,
    CreateClientInterface,
    consultRuc,
    createClient,
    getClientById,
    updateClient,
} from '../services/client'
import { useNavigate } from 'react-router-dom'
import {
    calcularDv,
    getDateInIsoFormat,
    validateEmail,
    validateRuc,
} from '../utils/maks'
import { ImageInterface } from '../pages/Accounts/Register'
import { CoinInfo, useCoin } from './coin_context'

export enum ClientDocumentType {
    RUC = 0,
    CedulaParaguaia = 1,
    Passaporte = 2,
    CedulaEstrangeira = 3,
    ComprovanteDeResidencia = 4,
}

export const ClientDocumentTypeLabel: Record<number, string> = {
    0: 'RUC',
    1: 'Cédula paraguaya',
    2: 'Pasaporte',
    3: 'Cédula extranjera',
    4: 'Carnet de residencia',
}

export interface ClientInfo {
    id?: number
    name: string
    document: string
    emails: string[]
    address: string
    nationality: NationalityEnum
    phoneNumbers: string[]
    allowCredit: boolean
    creditDays: string
    creditValue: number
    creditUsed: number
    extraCredit: number
    isActive: boolean
    fees: number
    chargeFees: boolean
    prefferedPrice: number | null
    expirationType: number
    documentType: number
    isPublicOrgan: boolean
    cpfCnpj: string | null
    nacionality: string
    zipCode: string | null
    number: string | null
    image?: ImageInterface | null
    imageBack?: ImageInterface | null
    taxFreeRegimenImage?: ImageInterface | null
    clientName?: string
    taxFreeRegimen: boolean
    taxFreeRegimenLimitDate: Date | null
    companyRegime: string
    companyEmissionDepartmentId: string
    companyEmissionDepartmentDescription: string
    companyEmissionCityId: string
    companyEmissionCityDescription: string
    companyEconomicActivityId: string
    companyEconomicActivityDescription: string
    companyEmissionHouseNumber: string
    companyEmissionDistrictId: string
    companyEmissionDistrictDescription: string
    showRemissionNoteData: boolean
    faturaExportacao: boolean
observation: string | null
    informacoesAdicionais: string | null
    employeeId?: number | null
    coin?: CoinInfo
}

export enum ExpirationType {
    DaysAfter = 0,
    FixedDay = 1,
}

export const anonymousClient: ClientInfo = {
    id: 0,
    name: 'Cliente anônimo',
    document: '',
    emails: [''],
    address: '',
    nationality: 1,
    phoneNumbers: [],
    allowCredit: false,
    creditDays: '',
    creditValue: 0,
    creditUsed: 0,
    extraCredit: 0,
    isActive: true,
    fees: 0,
    chargeFees: false,
    prefferedPrice: null,
    expirationType: ExpirationType.DaysAfter,
    documentType: ClientDocumentType.RUC,
    isPublicOrgan: false,
    cpfCnpj: null,
    nacionality: 'PRY',
    zipCode: null,
    number: null,
    taxFreeRegimen: false,
    taxFreeRegimenLimitDate: null,
    companyRegime: '',
    companyEmissionDepartmentId: '',
    companyEmissionDepartmentDescription: '',
    companyEmissionCityId: '',
    companyEmissionCityDescription: '',
    companyEconomicActivityId: '',
    companyEconomicActivityDescription: '',
    companyEmissionHouseNumber: '',
    companyEmissionDistrictId: '',
    companyEmissionDistrictDescription: '',
showRemissionNoteData: false,
    faturaExportacao: false,
    observation: null,
    informacoesAdicionais: null,
    employeeId: null,
}

export interface CreateClientInfo extends ClientInfo {
    step: number
    isEditing: boolean
}

export interface ClientErrorInfo {
    name: string
    document: string
    emails: string[]
    address: string
    phoneNumbers: string[]
    creditDays: string
    creditValue: string
    fees: string
    prefferedPrice: string
    cpfCnpj: string
}

const createClientInfoDefault: CreateClientInfo = {
    name: '',
    document: '',
    emails: [''],
    address: '',
    nationality: NationalityEnum.Native,
    phoneNumbers: [''],
    allowCredit: false,
    creditDays: '30',
    creditValue: 0,
    extraCredit: 0,
    step: 1,
    isEditing: false,
    creditUsed: 0,
    isActive: true,
    fees: 0,
    chargeFees: false,
    prefferedPrice: null,
    expirationType: ExpirationType.DaysAfter,
    documentType: ClientDocumentType.RUC,
    isPublicOrgan: false,
    cpfCnpj: null,
    nacionality: 'PRY',
    zipCode: null,
    number: null,
    taxFreeRegimen: false,
    taxFreeRegimenLimitDate: null,
    companyRegime: '',
    companyEmissionDepartmentId: '',
    companyEmissionDepartmentDescription: '',
    companyEmissionCityId: '',
    companyEmissionCityDescription: '',
    companyEconomicActivityId: '',
    companyEconomicActivityDescription: '',
    companyEmissionHouseNumber: '',
    companyEmissionDistrictId: '',
    companyEmissionDistrictDescription: '',
showRemissionNoteData: false,
    faturaExportacao: false,
    observation: null,
    informacoesAdicionais: null,
    employeeId: null,
}

export const clientErrorDefault: ClientErrorInfo = {
    name: '',
    document: '',
    emails: [''],
    address: '',
    phoneNumbers: [''],
    creditDays: '',
    creditValue: '',
    fees: '',
    prefferedPrice: '',
    cpfCnpj: '',
}

export interface ClientContextInterface {
    clientInfo: CreateClientInfo
    setClientInfo: Dispatch<SetStateAction<CreateClientInfo>>
    clientError: ClientErrorInfo
    setClientError: Dispatch<SetStateAction<ClientErrorInfo>>
    validate(client?: ClientInfo): boolean
    handleCreateNewClient(): Promise<ClientInfo | null>
    startNewClient(initialData?: Partial<ClientInfo>): void
    editClient(clientId: number): Promise<void>
    openRegisterClientModal: boolean
    setOpenRegisterClientModal: Dispatch<SetStateAction<boolean>>
    handleConsultRuc(ruc: string): void
    isConsultingRuc: boolean
}

const clientContext = createContext<ClientContextInterface>({
    clientInfo: createClientInfoDefault,
    setClientInfo: () => {},
    clientError: clientErrorDefault,
    setClientError: () => {},
    validate: () => {
        return false
    },
    handleCreateNewClient: async () => {
        return null
    },
    startNewClient: () => {},
    editClient: async () => {},
    openRegisterClientModal: false,
    setOpenRegisterClientModal: () => {},
    handleConsultRuc: () => {},
    isConsultingRuc: false,
})

export function useClient() {
    return useContext(clientContext)
}

interface Props {
    children: ReactNode
}

export const ClientProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const [clientInfo, setClientInfo] = useState<CreateClientInfo>(
        createClientInfoDefault
    )
    const [clientError, setClientError] =
        useState<ClientErrorInfo>(clientErrorDefault)
    const [openRegisterClientModal, setOpenRegisterClientModal] =
        useState(false)
    const timeoutRef = useRef<NodeJS.Timeout>()
    const [isConsultingRuc, setIsConsultingRuc] = useState(false)
    const { coins } = useCoin()

    const startNewClient = useCallback((initialData?: Partial<ClientInfo>) => {
        setClientInfo({ ...createClientInfoDefault, ...initialData })
        setClientError({ ...clientErrorDefault })
    }, [])

    const handleConsultRuc = useCallback((ruc: string) => {
        ruc = ruc.split('-')[0]
        if (!validateRuc(ruc)) return

        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(async () => {
            setIsConsultingRuc(true)
            const foundName = await consultRuc(ruc)
            if (foundName)
                setClientInfo((prev) => ({
                    ...prev,
                    name: foundName,
                    document: `${ruc}-${calcularDv(ruc)}`,
                }))
            setIsConsultingRuc(false)
        }, 500)
    }, [])

    const editClient = useCallback(
        async (id: number) => {
            try {
                const client = await getClientById(id, coins)

                setClientInfo(() => ({
                    ...client,
                    step: 1,
                    isEditing: true,
                }))
                setClientError({ ...clientErrorDefault })
            } catch (error) {
                console.log(error)
                snack.error('Cliente não encontrado')
                throw new Error('Client not found')
            }
        },
        [coins, snack]
    )

    const validate = useCallback(
        (client?: ClientInfo): boolean => {
            const clientToValidate = client ?? clientInfo
            let isOk = true
            const newClientErrorInfo = JSON.parse(
                JSON.stringify(clientErrorDefault)
            )

            if (!clientToValidate.name) {
                isOk = false
                newClientErrorInfo.name = 'Campo obrigatório'
            }
            if (!clientToValidate.document) {
                isOk = false
                newClientErrorInfo.document = 'Campo obrigatório'
            }
            /* if (
            clientToValidate.document &&
            !validateRuc(clientToValidate.document)
        ) {
            isOk = false
            newClientErrorInfo.document = 'Documento inválido'
        } */
            if (!clientToValidate.emails[0]) {
                isOk = false
                newClientErrorInfo.emails[0] = 'Campo obrigatório'
            }
            /* if (!clientToValidate.address) {
            isOk = false
            newClientErrorInfo.address = 'Campo obrigatório'
        } */
            if (!clientToValidate.phoneNumbers[0]) {
                isOk = false
                newClientErrorInfo.phoneNumbers[0] = 'Campo obrigatório'
            }
            if (clientToValidate.allowCredit && !clientToValidate.creditValue) {
                isOk = false
                newClientErrorInfo.creditValue = 'Campo obrigatório'
            }
            if (
                clientToValidate.allowCredit &&
                !parseInt(clientToValidate.creditDays)
            ) {
                isOk = false
                newClientErrorInfo.creditDays = 'Campo obrigatório'
            }

            for (let i = 0; i < clientToValidate.phoneNumbers.length; i++) {
                if (clientToValidate.phoneNumbers[i].length < 6) {
                    isOk = false
                    newClientErrorInfo.phoneNumbers[i] =
                        'Deve conter mais de 6 caracteres'
                }
                if (clientToValidate.phoneNumbers[i].length > 15) {
                    isOk = false
                    newClientErrorInfo.phoneNumbers[i] =
                        'Deve conter menos de 15 caracteres'
                }
            }

            for (let i = 0; i < clientToValidate.emails.length; i++) {
                if (
                    clientToValidate.emails[i] &&
                    !validateEmail(clientToValidate.emails[i])
                ) {
                    isOk = false
                    newClientErrorInfo.emails[i] = 'E-mail inválido'
                }
            }

            setClientError({ ...newClientErrorInfo })
            return isOk
        },
        [clientInfo]
    )

    const handleCreateNewClient =
        useCallback(async (): Promise<ClientInfo | null> => {
            let client: ClientInfo | null = null
            loading.show()
            try {
                const body: CreateClientInterface = {
                    nome: clientInfo.name,
                    numero_documento: clientInfo.document,
                    email: clientInfo.emails || null,
                    endereco: {
                        nome: clientInfo.address,
                        flag_nacionalidade: clientInfo.nationality,
                        codigo_postal: clientInfo.zipCode,
                        numero: clientInfo.number,
                    },
                    flag_ativo: clientInfo.isActive ? 1 : 0,
                    flag_fiado: clientInfo.allowCredit ? 1 : 0,
                    juros_credito_dia: parseInt(clientInfo.creditDays)
                        ? parseInt(clientInfo.creditDays)
                        : 0,
                    phones: clientInfo.phoneNumbers
                        .filter((p) => p)
                        .map((phoneNumber) => {
                            return { telefone: phoneNumber }
                        }),
                    valor_credito: clientInfo.creditValue,
                    taxa_juros: clientInfo.fees / 100,
                    flag_cobrar_juros: clientInfo.chargeFees ? 1 : 0,
                    preco_preferivel: clientInfo.prefferedPrice,
                    flag_tipo_vencimento: clientInfo.expirationType,
                    flag_tipo_documento: clientInfo.documentType,
                    flag_orgao_publico: Number(clientInfo.isPublicOrgan),
                    cpf_cnpj: clientInfo.cpfCnpj || null,
                    pais_nacionalidade: clientInfo.nacionality,
                    path_documento: clientInfo.image?.content ?? null,
                    flag_regime_isento_imposto: Number(
                        clientInfo.taxFreeRegimen
                    ),
                    data_limite_regime_isento_imposto:
                        clientInfo.taxFreeRegimenLimitDate
                            ? getDateInIsoFormat(
                                  clientInfo.taxFreeRegimenLimitDate
                              )
                            : null,
                    path_documento_verso: clientInfo.imageBack?.content ?? null,
                    path_documento_regime_isento_imposto:
                        clientInfo.taxFreeRegimenImage?.content ?? null,
                    regime_empresa: clientInfo.companyRegime || null,
                    cod_departamento_emissao_empresa:
                        clientInfo.companyEmissionDepartmentId || null,
                    desc_departamento_emissao_empresa:
                        clientInfo.companyEmissionDepartmentDescription || null,
                    cod_cidade_emissao_empresa:
                        clientInfo.companyEmissionCityId || null,
                    desc_cidade_emissao_empresa:
                        clientInfo.companyEmissionCityDescription || null,
                    cod_atividade_economica_empresa:
                        clientInfo.companyEconomicActivityId || null,
                    desc_atividade_economica_empresa:
                        clientInfo.companyEconomicActivityDescription || null,
                    num_casa_emissao_empresa:
                        clientInfo.companyEmissionHouseNumber || null,
                    cod_distrito_emissao_empresa:
                        clientInfo.companyEmissionDistrictId || null,
                    desc_distrito_emissao_empresa:
                        clientInfo.companyEmissionDistrictDescription || null,
                    flag_exibir_dados_nota_remissao: Number(
                        clientInfo.showRemissionNoteData
                    ),
                    flag_fatura_exportacao: Number(
                        clientInfo.faturaExportacao
                    ),
observacao: clientInfo.observation || null,
                    informacoes_adicionais: clientInfo.informacoesAdicionais || null,
                    id_moeda: clientInfo.coin?.id ?? 0,
                    id_funcionario: clientInfo.employeeId ?? null,
                }

                if (!clientInfo.isEditing) {
                    const id = await createClient(body)
                    snack.success('Cliente registrado com sucesso!')
                    client = {
                        ...clientInfo,
                        id: id,
                    }
                } else {
                    await updateClient({
                        ...body,
                        id_cliente: clientInfo.id ?? 0,
                    })
                    snack.success('Cliente editado com sucesso!')
                    client = {
                        ...clientInfo,
                    }
                }

                startNewClient()
            } catch (error) {
                const newClientError = { ...clientErrorDefault }
                if (error instanceof ClientEmailAlreadyInUseError) {
                } else if (error instanceof ClientDocumentAlreadyInUseError) {
                    newClientError.document = 'Este documento já está em uso'
                    setClientError(newClientError)
                } else snack.connectionFail(error)
            }
            loading.hide()
            return client
        }, [clientInfo, loading, snack, startNewClient])

    return (
        <clientContext.Provider
            value={useMemo(
                () => ({
                    clientInfo,
                    setClientInfo,
                    clientError,
                    setClientError,
                    validate,
                    handleCreateNewClient,
                    startNewClient,
                    editClient,
                    openRegisterClientModal,
                    setOpenRegisterClientModal,
                    handleConsultRuc,
                    isConsultingRuc,
                }),
                [
                    clientInfo,
                    setClientInfo,
                    clientError,
                    setClientError,
                    validate,
                    handleCreateNewClient,
                    startNewClient,
                    editClient,
                    openRegisterClientModal,
                    setOpenRegisterClientModal,
                    handleConsultRuc,
                    isConsultingRuc,
                ]
            )}
        >
            {children}
        </clientContext.Provider>
    )
}
